import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { gameAPI } from '../../services/api';
import { useTelegram } from '../../hooks/useTelegram';

const Page = styled.div`
  min-height: 100vh;
  background: #000;
  padding: 8px 16px;
  padding-bottom: 70px;
  font-family: 'GT Eesti', -apple-system, sans-serif;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 10px 0;
`;

const PillRow = styled.div`
  display: flex;
  background: rgba(255,255,255,0.1);
  border-radius: 18px;
  padding: 2px;
  margin-bottom: 8px;
`;

const Pill = styled.button`
  flex: 1;
  padding: 7px 0;
  border: none;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.$active ? '#007AFF' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'rgba(255,255,255,0.5)'};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  cursor: pointer;
  &:active { opacity: 0.7; }
`;

const CardImg = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 10px;
  flex-shrink: 0;
  overflow: hidden;
  background: #1a1a2e;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardInfo = styled.div`
  flex: 1;
`;

const CardPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
`;

const CardName = styled.div`
  font-size: 13px;
  color: rgba(255,255,255,0.5);
  margin-top: 2px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 12px 0 6px;
`;

const TIER_LABELS = {
  essential: 'PS Plus Essential',
  extra: 'PS Plus Extra',
  deluxe: 'PS Plus Deluxe',
};

const DURATIONS = [
  { key: '1m', label: '1 месяц' },
  { key: '3m', label: '3 месяца' },
  { key: '12m', label: '12 месяцев' },
];

const PSPlus = () => {
  const navigate = useNavigate();
  const { tg, hapticFeedback } = useTelegram();
  const [items, setItems] = useState([]);
  const [duration, setDuration] = useState('1m');

  useEffect(() => {
    const goBack = () => navigate('/');
    if (tg) {
      tg.setHeaderColor('#000000');
      tg.BackButton.show();
      tg.BackButton.onClick(goBack);
    }
    gameAPI.getPSPlus().then(setItems).catch(() => {});
    return () => {
      if (tg) {
        tg.BackButton.offClick(goBack);
        tg.BackButton.hide();
      }
    };
  }, [tg, navigate]);

  const formatPrice = (price) => {
    return price?.toLocaleString('ru-RU').replace(/\s/g, ' ');
  };

  // Get min price across all regions for tier+duration
  const getMinPrice = (tier, dur) => {
    const tierItems = items.filter(i => i.tier === tier && i.duration === dur);
    if (tierItems.length === 0) return null;
    return Math.min(...tierItems.map(i => i.price_rub));
  };

  // Get first item with image for tier+duration
  const getItemWithImage = (tier, dur) => {
    return items.find(i => i.tier === tier && i.duration === dur && i.image);
  };

  const psTiers = ['essential', 'extra', 'deluxe'];
  const psCards = psTiers
    .map(tier => {
      const minPrice = getMinPrice(tier, duration);
      const item = getItemWithImage(tier, duration);
      if (!minPrice) return null;
      return { tier, minPrice, image: item?.image };
    })
    .filter(Boolean);

  // EA Play min prices
  const eaMinPrice1m = getMinPrice('ea_play', '1m');
  const eaMinPrice12m = getMinPrice('ea_play', '12m');
  const eaItem1m = getItemWithImage('ea_play', '1m');
  const eaItem12m = getItemWithImage('ea_play', '12m');

  return (
    <Page>
      <Title>Подписки PS Plus</Title>

      <PillRow>
        {DURATIONS.map(d => (
          <Pill 
            key={d.key} 
            $active={duration === d.key} 
            onClick={() => { setDuration(d.key); hapticFeedback?.('light'); }}
          >
            {d.label}
          </Pill>
        ))}
      </PillRow>

      <CardList>
        {psCards.map(card => (
          <Card
            key={card.tier}
            onClick={() => {
              hapticFeedback?.('light');
              navigate(`/psplus/${card.tier}/${duration}`);
            }}
          >
            <CardImg>
              {card.image && <img src={card.image} alt="" loading="lazy" />}
            </CardImg>
            <CardInfo>
              <CardPrice>от {formatPrice(card.minPrice)} ₽</CardPrice>
              <CardName>{TIER_LABELS[card.tier]}</CardName>
            </CardInfo>
          </Card>
        ))}
      </CardList>

      <SectionTitle>Подписки EA Play</SectionTitle>
      
      <CardList>
        {eaMinPrice1m && (
          <Card
            onClick={() => {
              hapticFeedback?.('light');
              navigate('/psplus/ea_play/1m');
            }}
          >
            <CardImg>
              {eaItem1m?.image && <img src={eaItem1m.image} alt="" loading="lazy" />}
            </CardImg>
            <CardInfo>
              <CardPrice>от {formatPrice(eaMinPrice1m)} ₽</CardPrice>
              <CardName>1 месяц</CardName>
            </CardInfo>
          </Card>
        )}
        {eaMinPrice12m && (
          <Card
            onClick={() => {
              hapticFeedback?.('light');
              navigate('/psplus/ea_play/12m');
            }}
          >
            <CardImg>
              {eaItem12m?.image && <img src={eaItem12m.image} alt="" loading="lazy" />}
            </CardImg>
            <CardInfo>
              <CardPrice>от {formatPrice(eaMinPrice12m)} ₽</CardPrice>
              <CardName>12 месяцев</CardName>
            </CardInfo>
          </Card>
        )}
      </CardList>
    </Page>
  );
};

export default PSPlus;
